import React, { useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { AxiosError } from "axios";
import { useQuery } from "react-query";
import { useParams, useRouteMatch } from "react-router-dom";

import { ApiError } from "fe-shared/src/types/common";
import {
  Transaction,
  CorrectionRawResponse,
  TransactionType,
} from "fe-shared-transactions/src/types";

import { hasPermission } from "fe-shared/src/helpers/permissions";
import { Company, PermissionLevel } from "fe-shared/src/types/domain";

import {
  PERMISSIONS,
  TRANSACTIONS_QUERY_KEY,
  CORRECTIONS_QUERY_KEY,
} from "consts";

import TransactionDetails from "../TransactionDetails";
import * as api from "../modules/api";

interface Props {
  company: Company;
  setLastOpenedNumber: Dispatch<SetStateAction<string | null>>;
  apiBaseUrl?: string;
}

const TransactionDetailsContainer = ({
  company,
  setLastOpenedNumber,
  apiBaseUrl,
}: Props) => {
  const { transactionNumber } = useParams<{ transactionNumber: string }>();

  const { path } = useRouteMatch();
  const canViewTransactionDetails = hasPermission(company?.permissions, {
    name: PERMISSIONS.transactionDetails,
    level: PermissionLevel.Read,
  });

  const returnUrl = useMemo(() => path.replace("/:transactionNumber", ""), [
    path,
  ]);

  useEffect(() => {
    if (transactionNumber) {
      setLastOpenedNumber(transactionNumber);
    }
  }, [setLastOpenedNumber, transactionNumber]);

  const queryKey = [TRANSACTIONS_QUERY_KEY, transactionNumber];
  const transaction = useQuery<Transaction, AxiosError<ApiError>>(
    queryKey,
    api.getTransaction(apiBaseUrl),
    {
      enabled: canViewTransactionDetails,
    }
  );
  const linkedTransactions = transaction.data?.linkedTransactions;

  const correctionsQueryKey = [CORRECTIONS_QUERY_KEY, transactionNumber];
  const corrections = useQuery<CorrectionRawResponse, AxiosError<ApiError>>(
    correctionsQueryKey,
    api.getCorrections,
    {
      enabled: canViewTransactionDetails,
    }
  );

  const linkedAdjustmentNumber = useMemo(
    () =>
      linkedTransactions?.find(
        (item) => item.transactionType === TransactionType.Adjustment
      )?.transactionNumber,

    [linkedTransactions]
  );

  const linkedAdjustmentQueryKey = [
    TRANSACTIONS_QUERY_KEY,
    linkedAdjustmentNumber,
  ];
  const linkedAdjustment = useQuery<Transaction, AxiosError<ApiError>>(
    linkedAdjustmentQueryKey,
    api.getTransaction(apiBaseUrl),
    {
      keepPreviousData: true,
      enabled: canViewTransactionDetails && linkedAdjustmentNumber,
    }
  );

  return (
    <TransactionDetails
      company={company}
      returnUrl={returnUrl}
      transaction={transaction}
      corrections={corrections}
      linkedAdjustment={linkedAdjustment}
    />
  );
};

export default TransactionDetailsContainer;
